// Ambience recipes (docs/AUDIO_DESIGN.md §9): continuous beds fed by shared looped noise buffers, and one-shot events.
//   BEDS[name](ac, out, env, level, t0)  → { level(v, t), stop(t), advance(until, now) }
//   EVENTS[type](ac, out, when, ev, env) → registered voice
// env: { registry, tag, key() → current track key | null, echo (street echo input), r (seeded rng), raining }
import { clamp } from '../engine/core.js';
import { noise, crackle, wash, drops, hold } from './dsp.js';
import { snapToKey } from './harmony.js';

// Bed levels at level = 1 (calibrated so Paris sits around −32 dBFS RMS with the ambience slider at 0.6).
export const BED_MAX = { rain: 0.7, wind: 2.0, traffic: 0.44, water: 1.13, waves: 0.54, crickets: 0.09, cicadas: 2.4, birds: 0.17, crowd: 2.4 };
// Inside the rain bed: the gain of the wash, of the patter over it and of the loudest drip, and how far wash and patter
// sink between swells (1 = steady). Wash and patter are balanced so the bed is as loud as the one it replaced at every
// level (within 0.5 dB K-weighted, issue #70) and BED_MAX.rain stands: raising one means lowering the other.
export const RAIN = { body: 0.69, drops: 0.87, drips: 0.18, swell: 0.7 };
// One-shot event levels (x ev.gain).
export const EVENT_GAIN = { thunder: 1.0, bell: 0.14, chime: 0.24, train: 0.57, horn: 0.44, siren: 0.05, firework: 0.47, gull: 0.26, splash: 0.5, boat: 0.34 };
// Names on the city sounds mixer (one slider per bed and per event type a city uses).
export const SOUND_NAMES = {
  rain: 'Rain', wind: 'Wind', traffic: 'Traffic', water: 'Water', waves: 'Waves', crickets: 'Crickets', cicadas: 'Cicadas', birds: 'Birds', crowd: 'Crowd',
  thunder: 'Thunder', bell: 'Bells', chime: 'Chimes', train: 'Trains', horn: 'Ship horns', siren: 'Sirens', firework: 'Fireworks', gull: 'Gulls', splash: 'Splashes', boat: 'Boats',
};

/* ---------------- shared helpers ---------------- */
function kit(ac, env) {
  const nodes = [], src = [], procs = [];
  const r = env.r;
  const N = n => (nodes.push(n), n);
  const k = {
    r, nodes, src,
    G: (v = 1) => { const g = N(ac.createGain()); g.gain.value = v; return g; },
    F: (type, f, Q) => { const b = N(ac.createBiquadFilter()); b.type = type; b.frequency.value = f; if (Q != null) b.Q.value = Q; return b; },
    P: (p = 0) => { const s = N(ac.createStereoPanner()); s.pan.value = clamp(p, -1, 1); return s; },
    O: (type, f) => { const o = N(ac.createOscillator()); o.type = type; o.frequency.value = f; src.push(o); return o; },
    loop: (buf, rate = 1) => { const s = N(ac.createBufferSource()); s.buffer = buf; s.loop = true; s.playbackRate.value = rate; src.push(s); return s; },
    noise: (type, rate = 1) => k.loop(noise(ac, type), rate),
    /** periodic process: gap() → seconds until the next call of fn(t) */
    every(t0, gap, fn) { procs.push({ next: t0 + gap() * r(), gap, fn }); },
    start(t0) {
      for (const s of src) {
        if (s.buffer) s.start(t0, r() * s.buffer.duration); else s.start(t0);
      }
    },
    advance(until, now) {
      for (const p of procs) {
        if (p.next < now) p.next = now;                // a frozen main thread skips instead of bursting
        while (p.next < until) { p.fn(p.next); p.next += Math.max(0.02, p.gap()); }
      }
    },
    stop(t) { for (const s of src) { try { s.stop(t); } catch { /* not started */ } } },
  };
  return k;
}
/** Wrap a bed: output gain = level × BED_MAX, registered as one persistent voice. */
function bed(ac, out, env, name, level, t0, build) {
  const k = kit(ac, env), g = k.G(clamp(level, 0, 1) * BED_MAX[name]);
  g.connect(out);
  build(k, g, clamp(level, 0, 1));
  k.start(t0);
  const v = env.registry.add({ src: k.src, all: k.nodes, start: t0, end: Infinity, tag: `${env.tag}:bed` });
  return {
    name,
    level(x, t) { hold(g.gain, t); g.gain.setTargetAtTime(clamp(x, 0, 1) * BED_MAX[name], t, 0.5); },
    advance: (until, now) => k.advance(until, now),
    stop(t) { k.stop(t); v.end = t; },
  };
}
const expGap = (r, perSec) => -Math.log(1 - r() * 0.999) / Math.max(1e-3, perSec);
const span = (r, a, b) => a + r() * (b - a);
/** gated persistent voice hit: attack τ `atk`, release from t + `rel` with τ `tau` */
const gate = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };

/* ---------------- beds ---------------- */
export const BEDS = {
  rain(ac, out, env, level, t0) {
    return bed(ac, out, env, 'rain', level, t0, (k, g, lv) => {
      // the wash, a dark band that opens up with the level, and the patter over it: the drops loop twice, once slower,
      // so the pair only lines up again after 2.5 minutes
      const lp = k.F('lowpass', 1800 + 2200 * lv, 0.5), B = RAIN.body, D = RAIN.drops, lo = RAIN.swell;
      const body = k.G(B * (1 + lo) / 2), dropsG = k.G(D * (1 + lo) / 2);
      k.loop(wash(ac)).connect(lp); lp.connect(body); body.connect(g);
      for (const rate of [1, 0.84]) k.loop(drops(ac), rate).connect(dropsG);
      dropsG.connect(g);
      k.every(t0, () => span(k.r, 2.5, 6), t => {        // it comes and goes: a new strength every 2.5-6 s
        body.gain.setTargetAtTime(B * span(k.r, lo, 1), t, 1.4);
        dropsG.gain.setTargetAtTime(D * span(k.r, lo, 1), t, 0.9);
      });
      const drip = k.O('sine', 1200), dripG = k.G(0), dripPan = k.P(0);   // close drips ~1.5/s
      drip.connect(dripG); dripG.connect(dripPan); dripPan.connect(g);
      dripPan.pan.automationRate = 'k-rate';             // at a-rate this panner cost as much as the wash; its place is set 5 ms early
      k.every(t0, () => expGap(k.r, 1.5), t => {
        const f = span(k.r, 900, 1800);
        drip.frequency.setValueAtTime(f, t); drip.frequency.setTargetAtTime(f * 1.35, t, 0.02);
        dripPan.pan.setValueAtTime(span(k.r, -0.7, 0.7), Math.max(0, t - 0.005));
        gate(dripG.gain, RAIN.drips * span(k.r, 1 / 3, 1), t, 0.001, 0.004, 0.03);
      });
    });
  },
  wind(ac, out, env, level, t0) {
    return bed(ac, out, env, 'wind', level, t0, (k, g) => {
      for (const p of [-0.5, 0.5]) {                     // two chains, each wandering on its own
        const src = k.noise('brown'), bp = k.F('bandpass', 500, 3), wg = k.G(0.6), pan = k.P(p);
        src.connect(bp); bp.connect(wg); wg.connect(pan); pan.connect(g);
        k.every(t0, () => span(k.r, 2, 5), t => {
          bp.frequency.setTargetAtTime(span(k.r, 250, 900), t, 1.5);
          wg.gain.setTargetAtTime(span(k.r, 0.3, 1), t, 1.5);
        });
      }
    });
  },
  traffic(ac, out, env, level, t0) {
    return bed(ac, out, env, 'traffic', level, t0, (k, g, lv) => {
      const hum = k.noise('brown'), humLP = k.F('lowpass', 220, 0.7), humG = k.G(1);
      hum.connect(humLP); humLP.connect(humG); humG.connect(g);
      const air = k.noise('pink'), airBP = k.F('bandpass', 700, 1), airG = k.G(0.25);
      air.connect(airBP); airBP.connect(airG); airG.connect(g);
      const rate = Math.max(0.05, lv);                   // 4-10 passes per minute at level 1
      k.every(t0, () => span(k.r, 60 / (10 * rate), 60 / (4 * rate)), t => carPass(ac, g, t, env, k.r));
      k.every(t0, () => span(k.r, 60, 180) / clamp(lv * 2, 0.3, 1.5), t => carHorn(ac, g, t, env, k.r));
    });
  },
  water(ac, out, env, level, t0) {
    return bed(ac, out, env, 'water', level, t0, (k, g) => {
      const src = k.noise('brown'), bp = k.F('bandpass', 350, 1.2), wg = k.G(0.7);
      src.connect(bp); bp.connect(wg); wg.connect(g);
      k.every(t0, () => span(k.r, 0.4, 1.2), t => wg.gain.setTargetAtTime(span(k.r, 0.35, 1), t, 0.3));
      const plip = k.O('sine', 700), plipG = k.G(0);
      plip.connect(plipG); plipG.connect(g);
      k.every(t0, () => span(k.r, 1.5, 5), t => {
        const f = span(k.r, 500, 900);
        plip.frequency.setValueAtTime(f, t); plip.frequency.setTargetAtTime(f * 2, t, 0.02);
        gate(plipG.gain, span(k.r, 0.01, 0.03), t, 0.002, 0.006, 0.04);
      });
    });
  },
};

/* ---------------- traffic helpers: passing cars and car horns (one-shot voices) ---------------- */
function carPass(ac, out, t, env, r) {
  const dur = span(r, 5, 8), dir = r() < 0.5 ? 1 : -1, nodes = [];
  const N = n => (nodes.push(n), n);
  const src = N(ac.createBufferSource()); src.buffer = noise(ac, 'pink'); src.loop = true;
  const bp = N(ac.createBiquadFilter()); bp.type = 'bandpass'; bp.Q.value = 1.2;
  const eg = N(ac.createGain()), pan = N(ac.createStereoPanner()), vg = N(ac.createGain());   // vg: fades only
  bp.frequency.setValueAtTime(500, t); bp.frequency.linearRampToValueAtTime(800, t + dur / 2); bp.frequency.linearRampToValueAtTime(500, t + dur);
  const curve = new Float32Array(32), A = span(r, 0.5, 0.9);
  for (let i = 0; i < 32; i++) curve[i] = A * Math.pow(Math.sin(Math.PI * i / 31), 2);
  eg.gain.value = 0; eg.gain.setValueCurveAtTime(curve, t, dur);
  pan.pan.setValueAtTime(-0.8 * dir, t); pan.pan.linearRampToValueAtTime(0.8 * dir, t + dur);
  src.connect(bp); bp.connect(eg); eg.connect(pan); pan.connect(vg); vg.connect(out);
  const srcs = [src];
  if (env.raining) {                                   // tyre hiss on a wet road
    const hs = N(ac.createBufferSource()); hs.buffer = noise(ac, 'white'); hs.loop = true;
    const hp = N(ac.createBiquadFilter()); hp.type = 'highpass'; hp.frequency.value = 2500;
    const hg = N(ac.createGain()); hg.gain.value = 0.35;
    hs.connect(hp); hp.connect(hg); hg.connect(eg);
    hs.start(t, r() * 3); hs.stop(t + dur + 0.1); srcs.push(hs);
  }
  src.start(t, r() * 4); src.stop(t + dur + 0.1);
  env.registry.add({ src: srcs, all: nodes, start: t, end: t + dur + 0.1, tag: `${env.tag}:ev`, out: vg });
}
function carHorn(ac, out, t, env, r) {
  const key = env.key(), f = snapToKey(span(r, 330, 480), key), f2 = snapToKey(f * 1.26, key);
  const nodes = [], N = n => (nodes.push(n), n);
  const o1 = N(ac.createOscillator()), o2 = N(ac.createOscillator());
  o1.type = o2.type = 'sawtooth'; o1.frequency.value = f; o2.frequency.value = f2;
  const lp = N(ac.createBiquadFilter()); lp.type = 'lowpass'; lp.frequency.value = 1500;
  const eg = N(ac.createGain()), pan = N(ac.createStereoPanner()), echo = N(ac.createGain());
  eg.gain.value = 0; pan.pan.value = span(r, -0.8, 0.8); echo.gain.value = 0.5;
  o1.connect(lp); o2.connect(lp); lp.connect(eg); eg.connect(pan); pan.connect(out); pan.connect(echo); echo.connect(env.echo);
  const beeps = r() < 0.4 ? 2 : 1, A = 0.035;
  let end = t;
  for (let i = 0; i < beeps; i++) {
    const s = t + i * 0.32, d = i === beeps - 1 ? span(r, 0.25, 0.5) : 0.18;
    gate(eg.gain, A, s, 0.006, d, 0.02);
    end = s + d + 0.15;
  }
  for (const o of [o1, o2]) { o.start(t); o.stop(end); }
  env.registry.add({ src: [o1, o2], all: nodes, start: t, end, tag: `${env.tag}:ev`, out: eg });
}

Object.assign(BEDS, {
  waves(ac, out, env, level, t0) {
    return bed(ac, out, env, 'waves', level, t0, (k, g) => {
      const rum = k.noise('brown'), rumLP = k.F('lowpass', 150, 0.7), rumG = k.G(0.8);
      rum.connect(rumLP); rumLP.connect(rumG); rumG.connect(g);
      const chains = [0, 1].map(i => {                   // two alternating wave chains (automation only)
        const src = k.noise('pink'), lp = k.F('lowpass', 500, 0.6), cg = k.G(0.15), pan = k.P(i ? 0.35 : -0.35);
        const hs = k.noise('white'), hp = k.F('highpass', 3000, 0.7), hg = k.G(0);
        src.connect(lp); lp.connect(cg); cg.connect(pan); hs.connect(hp); hp.connect(hg); hg.connect(pan); pan.connect(g);
        return { lp, cg, hg };
      });
      let n = 0;
      k.every(t0, () => span(k.r, 7, 11), t => {
        const c = chains[n++ % 2];
        c.lp.frequency.setTargetAtTime(2600, t, 0.8); c.cg.gain.setTargetAtTime(1, t, 0.8);          // 2.5 s swell
        c.hg.gain.setTargetAtTime(0.3, t + 2.0, 0.2); c.hg.gain.setTargetAtTime(0, t + 2.6, 0.6);     // hiss at the crest
        c.lp.frequency.setTargetAtTime(500, t + 2.5, 1.7); c.cg.gain.setTargetAtTime(0.15, t + 2.5, 1.8); // 5 s wash
      });
    });
  },
  crickets(ac, out, env, level, t0) {
    return bed(ac, out, env, 'crickets', level, t0, (k, g) => {
      const n = 2 + (k.r() < 0.5 ? 1 : 0);
      [4300, 4600, 4900].slice(0, n).forEach((f, i) => {
        const o = k.O('sine', f), cg = k.G(0), pan = k.P([-0.6, 0.5, 0.1][i]);
        o.connect(cg); cg.connect(pan); pan.connect(g);
        k.every(t0, () => span(k.r, 0.55, 0.9), t => {
          for (let p = 0; p < 3; p++) gate(cg.gain, 1, t + p * 0.045, 0.002, 0.018, 0.004);   // three 18 ms pulses
        });
      });
    });
  },
  cicadas(ac, out, env, level, t0) {
    return bed(ac, out, env, 'cicadas', level, t0, (k, g) => {
      const src = k.noise('white'), bp = k.F('bandpass', 5200, 5), am = k.G(0.6), lfo = k.O('sine', 110), lfoG = k.G(0.4);
      const sw = k.G(0.1);
      src.connect(bp); bp.connect(am); am.connect(sw); sw.connect(g);
      lfo.connect(lfoG); lfoG.connect(am.gain);            // 110 Hz AM, depth .8
      k.every(t0, () => span(k.r, 10, 30), t => {
        const holdS = span(k.r, 6, 10);
        sw.gain.setTargetAtTime(1, t, 1.3);                // 4 s up
        sw.gain.setTargetAtTime(0.1, t + 4 + holdS, 1.7);  // 6-10 s hold, 5 s down
      });
    });
  },
  birds(ac, out, env, level, t0) {
    return bed(ac, out, env, 'birds', level, t0, (k, g, lv) => {
      const o = k.O('sine', 3200), bg = k.G(0), pan = k.P(0);
      o.connect(bg); bg.connect(pan); pan.connect(g);
      k.every(t0, () => expGap(k.r, (12 * Math.max(lv, 0.1)) / 60), t => {        // 12 phrases/min x level
        const notes = 2 + Math.floor(k.r() * 4), s = span(k.r, 0.85, 1.15);
        pan.pan.setValueAtTime(span(k.r, -0.8, 0.8), t);
        let x = t;
        for (let i = 0; i < notes; i++) {
          const d = span(k.r, 0.06, 0.12);
          o.frequency.setValueAtTime(3200 * s, x);
          o.frequency.linearRampToValueAtTime(4500 * s, x + d * 0.4);
          o.frequency.linearRampToValueAtTime(2800 * s, x + d);
          gate(bg.gain, span(k.r, 0.5, 1), x, 0.004, d - 0.012, 0.006);
          x += d + span(k.r, 0.03, 0.08);
        }
      });
    });
  },
  crowd(ac, out, env, level, t0) {
    return bed(ac, out, env, 'crowd', level, t0, (k, g) => {
      const src = k.noise('pink'), lp = k.F('lowpass', 3500, 0.6);
      lp.connect(g);
      for (const f of [500, 1400, 2600]) {                 // formant-ish bands wandering at syllable rate
        const bp = k.F('bandpass', f, 4), fg = k.G(0.3);
        src.connect(bp); bp.connect(fg); fg.connect(lp);
        k.every(t0, () => span(k.r, 0.12, 0.3), t => fg.gain.setTargetAtTime(0.1 + 0.9 * Math.pow(k.r(), 2), t, 0.06));
      }
    });
  },
});

/* ---------------- one-shot events ---------------- */
// Seconds each event lasts (joins part-way through only happen for events with an explicit `dur`).
export const EVENT_DUR = { thunder: 9, bell: 20, chime: 3.5, train: 14, horn: 2.9, siren: 10, firework: 4, gull: 2, splash: 1.2, boat: 20 };
const panOf = ev => {
  if (Array.isArray(ev.pan)) return ev.pan.map(v => clamp(+v || 0, -1, 1));
  if (typeof ev.pan === 'number') return [clamp(ev.pan, -1, 1)];
  if (typeof ev.x === 'number') return [clamp((ev.x / 480) * 2 - 1, -1, 1) * 0.8];
  return null;
};
/** Voice builder: envelopes run from t0 = when − offset (joins), sources start at `when`; vg is only used for fades. */
function oneShot(ac, out, when, ev, env, type) {
  const nodes = [], src = [], N = n => (nodes.push(n), n);
  const t0 = when - Math.max(0, ev.offset || 0);
  const vg = N(ac.createGain()), pan = N(ac.createStereoPanner());
  vg.gain.value = EVENT_GAIN[type] * clamp(ev.gain ?? 1, 0, 2);
  pan.connect(vg); vg.connect(out);
  const k = {
    t0, when, r: env.r, pan,
    G: (v = 0) => { const g = N(ac.createGain()); g.gain.value = v; return g; },
    F: (tp, f, Q) => { const b = N(ac.createBiquadFilter()); b.type = tp; b.frequency.value = f; if (Q != null) b.Q.value = Q; return b; },
    O: (tp, f) => { const o = N(ac.createOscillator()); o.type = tp; o.frequency.value = f; src.push(o); return o; },
    B: (buf, rate = 1) => { const s = N(ac.createBufferSource()); s.buffer = buf; s.loop = true; s.playbackRate.value = rate; src.push(s); return s; },
    /** pan: [p] fixed or [from, to] swept over dur (defaults used when the scene gives none) */
    panTo(p, dur) {
      const off = Math.min(1, Math.max(0, (when - t0) / dur));
      pan.pan.setValueAtTime(p.length > 1 ? p[0] + (p[1] - p[0]) * off : p[0], when);
      if (p.length > 1 && off < 1) pan.pan.linearRampToValueAtTime(p[1], t0 + dur);
    },
    /** envelope fn(u 0..1) over D seconds as a value curve on a fresh param, sliced from the join offset */
    curve(param, fn, D, n = 64) {
      const off = Math.max(0, when - t0);
      if (off >= D - 0.05) return;
      param.setValueCurveAtTime(envCurve(n, u => fn((off + u * (D - off)) / D)), when, D - off);
    },
    echo(amount) { const e = N(ac.createGain()); e.gain.value = amount; vg.connect(e); e.connect(env.echo); },   // post-level send
    finish(end) {
      if (end <= when + 0.05) { for (const n of nodes) { try { n.disconnect(); } catch { /* ok */ } } return null; }
      for (const s of src) { s.start(when, s.buffer ? env.r() * s.buffer.duration * 0.5 : 0); s.stop(end); }
      return env.registry.add({ src, all: nodes, start: when, end, tag: `${env.tag}:ev`, out: vg });
    },
  };
  return k;
}
/** Precomputed envelope for setValueCurveAtTime on a fresh gain (no overlap possible). */
const envCurve = (n, fn) => { const c = new Float32Array(n); for (let i = 0; i < n; i++) c[i] = fn(i / (n - 1)); return c; };
const swellFn = (D, rise, fall) => u => {
  const t = u * D;
  return t < rise ? Math.pow(Math.sin((t / rise) * Math.PI / 2), 2) : t > D - fall ? Math.pow(Math.sin(((D - t) / fall) * Math.PI / 2), 2) : 1;
};
const pentaMidi = (key, idx, lo = 72, hi = 84) => {
  const tonic = key ? key.tonic : 0, ivs = key && key.mode === 'major' ? [0, 2, 4, 7, 9] : [0, 3, 5, 7, 10];
  let m = lo + ((tonic - lo) % 12 + 12) % 12 + ivs[idx % 5];
  while (m > hi) m -= 12;
  return m;
};
const hz = m => 440 * Math.pow(2, (m - 69) / 12);

export const EVENTS = {
  thunder(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'thunder'), r = k.r, t0 = k.t0, D = 9;
    k.panTo(panOf(ev) || [r() * 1.2 - 0.6], D);
    const src = k.B(noise(ac, 'brown')), lp = k.F('lowpass', 900, 0.7), eg = k.G(0);
    lp.frequency.setValueAtTime(900, t0); lp.frequency.setTargetAtTime(140, t0, 2);
    const swells = Array.from({ length: 2 + (r() < 0.5 ? 1 : 0) }, () => [0.6 + r() * 1.9, 0.5 + 0.4 * r(), 0.15 + 0.2 * r()]);
    k.curve(eg.gain, u => {                                      // 0.25 s attack, swells kept under the first peak, τ 2.2 s
      const t = u * D;
      if (t < 0.25) return Math.pow(Math.sin((t / 0.25) * Math.PI / 2), 2);
      let e = Math.exp(-(t - 0.25) / 2.2);
      for (const [ts, a, w] of swells) e += a * (1 - Math.exp(-(ts - 0.25) / 2.2)) * 0.6 * Math.exp(-Math.pow((t - ts) / w, 2));
      return Math.min(t < 0.5 ? 1 : 0.7, e) * Math.min(1, (D - t) / 0.6);
    }, D, 180);
    src.connect(lp); lp.connect(eg); eg.connect(k.pan);
    const sub = k.O('sine', 48), sg = k.G(0);                  // sub sine 48 → 32 Hz
    sub.frequency.setValueAtTime(48, t0); sub.frequency.setTargetAtTime(32, t0, 1.5);
    sg.gain.setValueAtTime(0, t0); sg.gain.linearRampToValueAtTime(0.9, t0 + 0.3); sg.gain.setTargetAtTime(0, t0 + 0.3, 0.7);
    sub.connect(sg); sg.connect(k.pan);
    if (ev.near) {                                               // 60 ms crack
      const cs = k.B(noise(ac, 'white')), hp = k.F('highpass', 1500, 0.7), cg = k.G(0);
      cg.gain.setValueAtTime(0, t0); cg.gain.linearRampToValueAtTime(0.6, t0 + 0.003); cg.gain.setTargetAtTime(0, t0 + 0.01, 0.02);
      cs.connect(hp); hp.connect(cg); cg.connect(k.pan);
    }
    k.echo(0.15);
    return k.finish(t0 + D + 0.1);
  },
  bell(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'bell'), t0 = k.t0, f = snapToKey(165 * (ev.pitch || 1), env.key());
    k.panTo(panOf(ev) || [k.r() * 0.6 - 0.3], 1);
    const R = [0.5, 1, 1.19, 1.5, 2, 2.5], A = [0.35, 1, 0.5, 0.3, 0.45, 0.2], T = [6, 4, 3, 2.5, 2, 1.2];
    R.forEach((ratio, i) => {
      const o = k.O('sine', f * ratio * (1 + (k.r() - 0.5) * 0.002)), g = k.G(0);
      g.gain.setValueAtTime(0, t0); g.gain.linearRampToValueAtTime(A[i], t0 + 0.004);
      g.gain.setTargetAtTime(0, t0 + 0.004, T[i]); g.gain.setTargetAtTime(0, t0 + 16, 0.8);
      o.connect(g); g.connect(k.pan);
    });
    k.echo(0.6);
    return k.finish(t0 + 20.5);
  },
  chime(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'chime'), t0 = k.t0, key = env.key();
    k.panTo(panOf(ev) || [k.r() * 0.6 - 0.3], 1);
    [2, 1, 0, 3].forEach((deg, i) => {                           // scale degrees 3-2-1-5, 0.7 s apart
      const f = hz(pentaMidi(key, deg)), t = t0 + i * 0.7;
      const car = k.O('sine', f), mod = k.O('sine', f * 3.5), mg = k.G(0), g = k.G(0);
      mg.gain.setValueAtTime(2 * f * 3.5, t); mg.gain.setTargetAtTime(0, t, 0.6);
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.6, t + 0.003); g.gain.setTargetAtTime(0, t + 0.003, 0.9);
      mod.connect(mg); mg.connect(car.frequency); car.connect(g); g.connect(k.pan);
    });
    k.echo(0.4);
    return k.finish(t0 + 2.1 + 3.5);
  },
  train(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'train'), t0 = k.t0, D = Math.max(4, ev.dur || 14), r = k.r;
    const dir = r() < 0.5 ? 1 : -1;
    k.panTo(panOf(ev) || [-0.7 * dir, 0.7 * dir], D);
    const src = k.B(noise(ac, 'brown')), lp = k.F('lowpass', 280, 0.7), eg = k.G(0);
    k.curve(eg.gain, swellFn(D, Math.min(3, D * 0.3), Math.min(4, D * 0.35)), D);
    src.connect(lp); lp.connect(eg); eg.connect(k.pan);
    const co = k.O('sine', 90), cg = k.G(0), ks = k.B(noise(ac, 'white')), khp = k.F('highpass', 2000, 0.7), kg = k.G(0);
    co.connect(cg); cg.connect(eg); ks.connect(khp); khp.connect(kg); kg.connect(eg);
    for (let tc = t0 + 0.4; tc < t0 + D - 0.4; tc += 1.1) for (const d of [0, 0.14]) {   // clack pairs
      const t = tc + d;
      if (t < when - 0.01) continue;
      co.frequency.setValueAtTime(90, t); co.frequency.setTargetAtTime(55, t, 0.02);
      gate(cg.gain, 0.6, t, 0.002, 0.004, 0.03); gate(kg.gain, 0.25, t, 0.0005, 0.002, 0.006);
    }
    return k.finish(t0 + D + 0.1);
  },
  horn(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'horn'), t0 = k.t0, f = snapToKey(span(k.r, 90, 130), env.key());
    k.panTo(panOf(ev) || [k.r() * 1.2 - 0.6], 1);
    const a = k.O('sawtooth', f), b = k.O('sawtooth', f * 1.005), sq = k.O('square', 2 * f), sqg = k.G(0.3);
    const lp = k.F('lowpass', 700, 0.7), eg = k.G(0);
    a.connect(lp); b.connect(lp); sq.connect(sqg); sqg.connect(lp); lp.connect(eg); eg.connect(k.pan);
    eg.gain.setValueAtTime(0, t0); eg.gain.linearRampToValueAtTime(0.5, t0 + 0.15);
    eg.gain.setValueAtTime(0.5, t0 + 1.95); eg.gain.linearRampToValueAtTime(0, t0 + 2.75);
    k.echo(0.5);
    return k.finish(t0 + 2.85);
  },
  siren(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'siren'), t0 = k.t0, D = Math.max(3, ev.dur || 10);
    k.panTo(panOf(ev) || [k.r() * 1.4 - 0.7], D);
    const o = k.O('triangle', 650), lp = k.F('lowpass', 2000, 0.7), eg = k.G(0);
    let t = t0, up = true;
    while (t + 2.4 < when) { t += 2.4; up = !up; }             // joined part-way: continue the wail where it is
    const u = (when - t) / 2.4, from = up ? 650 : 1350, to = up ? 1350 : 650;
    o.frequency.setValueAtTime(from + (to - from) * u, when);
    for (t += 2.4; t < t0 + D + 2.4; t += 2.4, up = !up) o.frequency.linearRampToValueAtTime(up ? 1350 : 650, t);
    k.curve(eg.gain, swellFn(D, 1, 1.5), D);
    o.connect(lp); lp.connect(eg); eg.connect(k.pan);
    k.echo(0.6);
    return k.finish(t0 + D + 0.1);
  },
  firework(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'firework'), t0 = k.t0;
    k.panTo(panOf(ev) || [k.r() * 1.2 - 0.6], 1);
    const th = k.B(noise(ac, 'brown')), tlp = k.F('lowpass', 300, 0.7), tg = k.G(0);
    tg.gain.setValueAtTime(0, t0); tg.gain.linearRampToValueAtTime(1, t0 + 0.005); tg.gain.setTargetAtTime(0, t0 + 0.005, 0.5);
    th.connect(tlp); tlp.connect(tg); tg.connect(k.pan);
    const o = k.O('sine', 70), og = k.G(0);
    o.frequency.setValueAtTime(70, t0); o.frequency.setTargetAtTime(40, t0, 0.15);
    og.gain.setValueAtTime(0, t0); og.gain.linearRampToValueAtTime(0.8, t0 + 0.005); og.gain.setTargetAtTime(0, t0 + 0.005, 0.3);
    o.connect(og); og.connect(k.pan);
    const cr = k.B(crackle(ac), 1.6), chp = k.F('highpass', 2500, 0.7), cg = k.G(0);
    cg.gain.setValueAtTime(0, t0 + 0.3); cg.gain.linearRampToValueAtTime(0.8, t0 + 0.33); cg.gain.setTargetAtTime(0, t0 + 0.33, 0.6);
    cr.connect(chp); chp.connect(cg); cg.connect(k.pan);
    k.echo(0.5);
    return k.finish(t0 + 4);
  },
  gull(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'gull'), t0 = k.t0, s = span(k.r, 0.9, 1.1);
    k.panTo(panOf(ev) || [k.r() * 1.4 - 0.7], 1);
    const o = k.O('sawtooth', 1200), bp = k.F('bandpass', 1900, 3), cg = k.G(0), lfo = k.O('sine', 22), lg = k.G(40);
    lfo.connect(lg); lg.connect(o.detune); o.connect(bp); bp.connect(cg); cg.connect(k.pan);
    let t = t0;
    const calls = 2 + Math.floor(k.r() * 3);
    for (let i = 0; i < calls; i++) {
      o.frequency.setValueAtTime(1200 * s, t); o.frequency.linearRampToValueAtTime(2100 * s, t + 0.07); o.frequency.linearRampToValueAtTime(900 * s, t + 0.32);
      gate(cg.gain, 1, t, 0.01, 0.26, 0.03);
      t += span(k.r, 0.28, 0.4) + 0.1;
    }
    k.echo(0.4);
    return k.finish(t + 0.5);
  },
  splash(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'splash'), t0 = k.t0;
    k.panTo(panOf(ev) || [k.r() * 1.2 - 0.6], 1);
    const n = k.B(noise(ac, 'white')), bp = k.F('bandpass', 900, 1.5), g = k.G(0);
    bp.frequency.setValueAtTime(900, t0); bp.frequency.linearRampToValueAtTime(2200, t0 + 0.25);
    g.gain.setValueAtTime(0, t0); g.gain.linearRampToValueAtTime(1, t0 + 0.01); g.gain.setTargetAtTime(0, t0 + 0.01, 0.12);
    n.connect(bp); bp.connect(g); g.connect(k.pan);
    const d = k.O('sine', 2000), dg = k.G(0);
    d.connect(dg); dg.connect(k.pan);
    const drops = 3 + Math.floor(k.r() * 3);
    for (let i = 0; i < drops; i++) {
      const t = t0 + span(k.r, 0.08, 0.6);
      d.frequency.setValueAtTime(span(k.r, 1500, 3500), t); gate(dg.gain, 0.4, t, 0.001, 0.003, 0.015);
    }
    k.echo(0.3);
    return k.finish(t0 + 1.2);
  },
  boat(ac, out, when, ev, env) {
    const k = oneShot(ac, out, when, ev, env, 'boat'), t0 = k.t0, D = Math.max(4, ev.dur || 20);
    const dir = k.r() < 0.5 ? 1 : -1;
    k.panTo(panOf(ev) || [-0.6 * dir, 0.6 * dir], D);
    const o = k.O('square', 42), lp = k.F('lowpass', 160, 0.7), am = k.G(0.6), lfo = k.O('sine', 7), lg = k.G(0.4), eg = k.G(0);
    lfo.connect(lg); lg.connect(am.gain);
    k.curve(eg.gain, u => Math.pow(Math.sin(Math.PI * u), 2), D);
    o.connect(lp); lp.connect(am); am.connect(eg); eg.connect(k.pan);
    return k.finish(t0 + D + 0.1);
  },
};
